import type { ReactNode } from 'react'

import { useMemo, useState } from 'react'
import { ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/lib/clinic/store'
import { usePhoneFormatter } from '@/hooks/use-clinic-helpers'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import PatientAvatar from '@/components/app/patient-avatar'

import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '@/components/ui/command'

interface PatientPickerProps {
    value: string
    onChange: (patientId: string) => void
    id?: string
    invalid?: boolean
    disabled?: boolean
    className?: string
}

const PatientPicker = ({ value, onChange, id, invalid, disabled, className }: PatientPickerProps): ReactNode => {
    const { data } = useClinic()
    const formatPhone = usePhoneFormatter()
    const [open, setOpen] = useState(false)
    const selected = data.patients.find(patient => patient.id === value)
    const patients = useMemo(() => [...data.patients].sort((a, b) => a.name.localeCompare(b.name)), [data.patients])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
                render={(
                    <Button
                        id={id}
                        type='button'
                        variant='outline'
                        role='combobox'
                        aria-expanded={open}
                        aria-invalid={invalid || undefined}
                        disabled={disabled}
                        className={cn('w-full justify-between bg-card px-3 font-normal hover:bg-card', className)}
                    />
                )}
            >
                {selected
                    ? (
                        <span className='flex min-w-0 items-center gap-2'>
                            <span className='truncate text-foreground'>{selected.name}</span>
                            <span className='shrink-0 text-xs text-muted-foreground'>{selected.code}</span>
                        </span>
                    )
                    : <span className='truncate text-muted-foreground'>Search by name, phone or patient ID</span>}

                <ChevronsUpDown className='size-4 shrink-0 text-muted-foreground' aria-hidden='true' />
            </PopoverTrigger>

            <PopoverContent align='start' className='w-(--anchor-width) min-w-[18rem] p-0'>
                <Command>
                    <CommandInput placeholder='Name, phone or patient ID' />
                    <CommandList>
                        <CommandEmpty>No patient matches that search.</CommandEmpty>
                        <CommandGroup>
                            {patients.map(patient => (
                                <CommandItem
                                    key={patient.id}
                                    value={`${patient.name} ${patient.code} ${patient.phone} ${patient.id}`}
                                    data-checked={patient.id === value}
                                    onSelect={() => {
                                        onChange(patient.id)
                                        setOpen(false)
                                    }}
                                >
                                    <PatientAvatar name={patient.name} size='sm' className='size-7 text-[11px]' />
                                    <span className='flex min-w-0 flex-col'>
                                        <span className='truncate font-medium'>{patient.name}</span>
                                        <span className='truncate text-xs text-muted-foreground'>
                                            {patient.code} · {formatPhone(patient.phone)}
                                        </span>
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}

export default PatientPicker
